// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { BodyText, TitleText } from '@calendly/ui/components/typography'
import { colorTextSecondary } from '@calendly/ui/theme'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'

import { useGetPolicyQuery } from 'common'
import LoadingState from 'components/core/loading_state'
import { TabContainer, TabContent, PageHeaderWithTabs } from 'components/page_elements'
import { SharedEventTypes, Workflows, Invitations, Payments } from 'features/organization_settings'
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context'
import { useMeetingTypesPath } from 'hooks/use_meeting_types_path'

import { generateTabs } from '../../helpers'
import { getAdminPermissionsTabs } from '../../settings_section_tabs'

const SharedEventTypesTab = () => (
  <TabContainer narrow>
    <TabContent>
      <TitleText level={2} size="small">
        {I18n.t('publisher.organization_settings.shared_event_types.subtitle')}
      </TitleText>
      <SharedEventTypes />
    </TabContent>
  </TabContainer>
)

const WorkflowsTab = () => (
  <TabContainer narrow>
    <TabContent>
      <TitleText level={2} size="small">
        {I18n.t('publisher.organization_settings.workflows.subtitle')}
      </TitleText>
      <Workflows />
    </TabContent>
  </TabContainer>
)

const InvitationsTab = () => (
  <TabContainer narrow>
    <TabContent>
      <Invitations />
    </TabContent>
  </TabContainer>
)

const PaymentsTab = () => (
  <TabContainer narrow>
    <TabContent>
      <TitleText level={2} size="small">
        {I18n.t('publisher.organization_settings.payments.question')}
      </TitleText>
      <Payments />
    </TabContent>
  </TabContainer>
)

export const PermissionsPage = ({ match }) => {
  const { pathname } = useLocation()
  const inSettingsModal = useIsSettingsModalLayout()
  /** Settings modal shell renders tab row (desktop + mobile); omit duplicate header here. */
  const settingsModalShellOwnsTabs = inSettingsModal
  const isNewBadgeHidden = useFlag('remove_new_flags_comp')

  const { data: policyData = {}, isFetching: isPolicyFetching } = useGetPolicyQuery()
  const {
    canManageInvitationPermissions,
    canManageOrganizationEventTypeSettings,
    canManageWorkflows,
    canManagePayments
  } = policyData || {}
  const meetingTypesPath = useMeetingTypesPath()
  const showPayments = useFlag('commerce_admin_payment_permissions_feature') && canManagePayments

  const tabs = getAdminPermissionsTabs(isNewBadgeHidden, { showPayments })

  if (isPolicyFetching) {
    return <LoadingState />
  }

  const routes = (
    <Switch>
      <Redirect from={match.url} to={`${match.url}/event_types`} exact />
      {canManageOrganizationEventTypeSettings && (
        <Route path={`${match.url}/event_types`} component={SharedEventTypesTab} />
      )}
      {canManageWorkflows && <Route path={`${match.url}/workflows`} component={WorkflowsTab} />}
      {canManageInvitationPermissions && <Route path={`${match.url}/invitations`} component={InvitationsTab} />}
      {showPayments && <Route path={`${match.url}/payments`} component={PaymentsTab} />}
      <Redirect to={meetingTypesPath} />
    </Switch>
  )

  if (settingsModalShellOwnsTabs) {
    return routes
  }

  return (
    <>
      <PageHeaderWithTabs
        hideTitleChrome={inSettingsModal}
        eyebrow={(
          <BodyText weight="bold" color={colorTextSecondary}>
            {I18n.t('publisher.admin.navigation.organization')}
          </BodyText>
        )}
        title={I18n.t('publisher.admin.permissions.title')}
        tabs={generateTabs({ pathname, tabs })}
      />
      {routes}
    </>
  )
}
